import { GridToolbarProps, ToolbarPropsOverrides, useGridApiContext, Toolbar } from '@mui/x-data-grid-pro';
import { FormControlLabel, Switch, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { AssignDriverToolbarFilters, emptyRowSet } from './types';
import { useEffect, useState } from 'react';
import { IconButton } from '@mui/material';
import { ChevronLeftTwoTone } from '@mui/icons-material';
import { HAZMAT_ENDORSED_FILTER, ON_ROUTE_NO_JOB_FILTER, UNASSIGNED_FILTER } from './FilterItems';
import { StyledQuickFilter } from './StyledComponents';

import styles from './Driver/DataGrid.Driver.module.css';

export default function AssignDriverGridToolbar({
	driverToolbarOverrides: { defaultFilterModel, slickRef }
}: GridToolbarProps & ToolbarPropsOverrides) {
	const gridApi = useGridApiContext();
	const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

	const [{ unassigned, onRouteWithNoJobs, hazmatEndorsed, filterModel }, setFilters] = useState<AssignDriverToolbarFilters>({
		unassigned: Boolean(defaultFilterModel?.items.find((filter) => filter.id === UNASSIGNED_FILTER.id)),
		onRouteWithNoJobs: Boolean(defaultFilterModel?.items.find((filter) => filter.id === ON_ROUTE_NO_JOB_FILTER.id)),
		hazmatEndorsed: Boolean(defaultFilterModel?.items.find((filter) => filter.id === HAZMAT_ENDORSED_FILTER.id)),
		filterModel: { items: [], ...defaultFilterModel }
	});

	const onUnassignedCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.checked) {
			setFilters((state) => ({
				...state,
				unassigned: event.target.checked,
				filterModel: {
					items: [...state.filterModel.items, { ...UNASSIGNED_FILTER }]
				}
			}));
		} else {
			setFilters((state) => ({
				...state,
				unassigned: event.target.checked,
				filterModel: {
					items: [...state.filterModel.items.filter((f) => f.field !== UNASSIGNED_FILTER.field)]
				}
			}));
		}
	};

	const onRouteNoJobCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.checked) {
			setFilters((state) => ({
				...state,
				onRouteWithNoJobs: event.target.checked,
				filterModel: {
					items: [...state.filterModel.items, { ...ON_ROUTE_NO_JOB_FILTER }]
				}
			}));
		} else {
			setFilters((state) => ({
				...state,
				onRouteWithNoJobs: event.target.checked,
				filterModel: {
					items: [...state.filterModel.items.filter((f) => f.field !== ON_ROUTE_NO_JOB_FILTER.field)]
				}
			}));
		}
	};

	const onHazmatCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.checked) {
			setFilters((state) => ({
				...state,
				hazmatEndorsed: event.target.checked,
				filterModel: {
					items: [...state.filterModel.items, { ...HAZMAT_ENDORSED_FILTER }]
				}
			}));
		} else {
			setFilters((state) => ({
				...state,
				hazmatEndorsed: event.target.checked,
				filterModel: {
					items: [...state.filterModel.items.filter((f) => f.field !== HAZMAT_ENDORSED_FILTER.field)]
				}
			}));
		}
	};

	/** When the local filter model changes, update the DataGrid API */
	useEffect(() => {
		gridApi.current.setFilterModel({
			...filterModel,
			quickFilterValues: []
		});
		gridApi.current.setRowSelectionModel(emptyRowSet);
	}, [gridApi, filterModel]);

	return (
		<Toolbar className={styles.toolbar}>
			<div className={styles.items}>
				{slickRef?.current !== undefined && (
					<IconButton
						className={styles['previous-icon-btn']}
						onClick={() => {
							slickRef.current?.slickPrev();
						}}
					>
						<ChevronLeftTwoTone />
					</IconButton>
				)}
				<FormControlLabel
					label='Not Assigned to Any Current Load'
					control={
						<Switch
							className={styles['switch']}
							size='small'
							checked={unassigned}
							onChange={onUnassignedCheckChange}
						/>
					}
					slotProps={{
						typography: {
							className: styles.label
						}
					}}
				/>

				<FormControlLabel
					label='On Route with No Other Jobs'
					control={
						<Switch
							size='small'
							checked={onRouteWithNoJobs}
							onChange={onRouteNoJobCheckChange}
						/>
					}
					slotProps={{
						typography: {
							className: styles.label
						}
					}}
				/>

				<FormControlLabel
					label='Hazmat Endorsed'
					control={
						<Switch
							size='small'
							checked={hazmatEndorsed}
							onChange={onHazmatCheckChange}
						/>
					}
					slotProps={{
						typography: {
							className: styles.label
						}
					}}
				/>
			</div>

			<div className={styles['right-justified']}>
				<StyledQuickFilter
					className={styles['quick-filter']}
					inputClassName={styles['quick-filter-input']}
					defaultExpanded={isMobile}
					alwaysExpanded={isMobile ? true : undefined}
				/>
			</div>
		</Toolbar>
	);
}
